import type { AxiosInstance } from "axios";
import {
  AlertCircle,
  AlertTriangle,
  BarChart3,
  Check,
  CheckCircle2,
  Copy,
  Info,
  Loader2,
  RefreshCw,
  Search,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { FormulaList, HighlightCard, HighlightGrid, noteText } from "../components/ReportGuide/GuideContent.js";
import { ReportGuideAccordion } from "../components/ReportGuide/ReportGuideAccordion.js";
import { ReportGuideToggleButton } from "../components/ReportGuide/ReportGuideToggleButton.js";
import { Button } from "../components/ui/button.js";
import { InfoTooltip } from "../components/ui/info-tooltip.js";
import { Input } from "../components/ui/input.js";
import { Select } from "../components/ui/select.js";
import { Skeleton } from "../components/ui/skeleton.js";
import { apiErrorMessage } from "../lib/api.js";
import { cn } from "../lib/cn.js";
import { TableResizeHandle, useTableResize } from "../components/TableResizeHandle.js";
import { formatCount, formatMoney } from "../lib/format.js";
import { validateUtmParameters, type UtmValidationResult } from "../lib/utm-validation.js";

/**
 * The same all-time span `ecom_code`'s UtmChecker hardcodes (`from: "2024-01-01", to:
 * dayjs().format(...)`), ported verbatim rather than a rolling window: this screen has no
 * date picker, and "since we started tracking" is the only range that makes "Impact"
 * mean what its label says — the total cost of an ad's broken tracking, not last month's.
 */
const IMPACT_WINDOW_START = "2024-01-01";
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * UTM Checker — audits the tracking parameters on every actively-delivering Meta and
 * Google ad. Ported from `ecom_code`'s `UtmChecker.jsx` + `UtmUrlValidationTable.jsx`:
 * the interaction model (platform switch, agent selection flow, the fix modal) is
 * legacy's; the table, type scale and colour tokens are this platform's (`ReportTable`
 * / `styles.css`), so it reads as the same product on both dashboards.
 */

interface AdApiRow {
  readonly ad_id: string;
  readonly ad_name: string;
  readonly campaign_id: string;
  readonly campaign_name: string;
  readonly adset_id: string;
  readonly adset_name: string;
  readonly status: string;
  readonly effective_status: string;
  readonly url_tags: string;
  readonly channel: string;
  readonly campaign_type?: string;
  readonly advertising_channel_type?: number;
  /** Ad-platform spend, over `IMPACT_WINDOW_START` to today. */
  readonly spend: number;
  /** Orders whose last click matched this ad, over that same window. */
  readonly lc_orders: number;
  readonly lc_revenue: number;
  /** Google only: the Google Ads customer this ad belongs to — needed to auto-fix it. '' for Meta. */
  readonly customer_id: string;
}

interface ProcessedAdRow {
  readonly id: string;
  readonly name: string;
  readonly campaignName: string;
  readonly campaignId: string;
  readonly adsetId: string;
  readonly adsetName: string;
  readonly urlTags: string;
  readonly status: string;
  readonly channel: "meta" | "google";
  readonly campaignType?: string;
  readonly customerId: string;
  readonly validation: UtmValidationResult;
  readonly spend: number;
  readonly lcOrders: number;
  readonly lcRevenue: number;
}

interface ToastNotice {
  readonly id: string;
  readonly message: string;
  readonly type: "success" | "info" | "error";
}

type FilterMode = "all" | "flagged" | "correct";

const PAGE_SIZES = [10, 20, 50, 100] as const;

export function UtmCheckerScreen({
  client,
  tenantId,
  storeName,
}: {
  readonly client: AxiosInstance;
  readonly tenantId: string;
  readonly storeName: string;
}): React.JSX.Element {
  const [platform, setPlatform] = useState<"meta" | "google">("meta");
  const [ads, setAds] = useState<readonly AdApiRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  // Defaults to the ads that need attention, not the unfiltered list — ported from
  // ecom_code's UtmUrlValidationTable, which opens the same way.
  const [filterMode, setFilterMode] = useState<FilterMode>("flagged");
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const initialWidths = useMemo(
    () => (isSelectionMode ? [48, 180, 280, 260, 220] : [180, 280, 260, 220]),
    [isSelectionMode],
  );
  const { colWidths, startResize } = useTableResize(initialWidths);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [autoFixLoading, setAutoFixLoading] = useState(false);
  const [autoFixProgress, setAutoFixProgress] = useState<string>("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const [selectedAd, setSelectedAd] = useState<ProcessedAdRow | null>(null);
  const [copied, setCopied] = useState(false);

  const [toasts, setToasts] = useState<readonly ToastNotice[]>([]);
  const [showGuideSection, setShowGuideSection] = useState(false);

  const showToast = useCallback((message: string, type: ToastNotice["type"] = "success") => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000);
  }, []);

  const loadAds = useCallback(
    async (isManualSync = false) => {
      if (isManualSync) setSyncing(true);
      else setLoading(true);
      setError(null);
      try {
        const res = await client.get<{
          success: boolean;
          data: { platform: "meta" | "google"; total: number; ads: readonly AdApiRow[] };
        }>("/api/v2/utm-checker/ads", {
          params: { platform, limit: 5000, activeOnly: "true", from: IMPACT_WINDOW_START, to: todayIso() },
        });
        setAds(res.data.data.ads ?? []);
        if (isManualSync) {
          showToast(`Synced active ${platform === "meta" ? "Meta" : "Google"} ads — re-auditing parameters…`, "success");
        }
      } catch (caught) {
        setError(
          apiErrorMessage(caught, "Could not load ads for the UTM audit. Check that the store's ad accounts are connected."),
        );
        setAds([]);
      } finally {
        setLoading(false);
        setSyncing(false);
      }
    },
    [client, platform, showToast],
  );

  const handleSyncAndReaudit = async (): Promise<void> => {
    if (syncing) return;
    setSyncing(true);
    setSyncProgress("Preparing sync…");
    try {
      const activeAdIds = ads.map((a) => a.ad_id).filter(Boolean);
      if (activeAdIds.length === 0) {
        showToast("No active ads to sync.", "info");
        return;
      }

      const SYNC_BATCH_SIZE = 50;
      let totalUpdated = 0;
      const totalAds = activeAdIds.length;

      for (let i = 0; i < totalAds; i += SYNC_BATCH_SIZE) {
        const chunk = activeAdIds.slice(i, i + SYNC_BATCH_SIZE);
        const processedCount = Math.min(i + SYNC_BATCH_SIZE, totalAds);
        if (totalAds > SYNC_BATCH_SIZE) {
          setSyncProgress(`Syncing (${processedCount}/${totalAds})…`);
        }

        const res = await client.post<{ success: boolean; data: { updatedCount: number } }>(
          "/api/v2/utm-checker/sync-ads",
          {
            platform,
            adIds: chunk,
          },
        );
        totalUpdated += res.data.data.updatedCount;
      }

      showToast(`Synced ${totalUpdated} active ${platform === 'meta' ? 'Meta' : 'Google'} ads — re-auditing parameters…`, 'success');
      await loadAds(false);
    } catch (caught) {
      showToast(apiErrorMessage(caught, 'Could not sync ads from platform. Try again.'), 'error');
    } finally {
      setSyncing(false);
      setSyncProgress("");
    }
  };

  useEffect(() => {
    void loadAds(false);
    setIsSelectionMode(false);
    setSelectedIds(new Set());
    setSearch("");
    setFilterMode("flagged");
    setPage(1);
  }, [loadAds, platform, tenantId]);

  const processedRows: readonly ProcessedAdRow[] = useMemo(
    () =>
      ads.map((ad) => ({
        id: ad.ad_id,
        name: ad.ad_name || "Untitled ad",
        campaignName: ad.campaign_name || "Unassigned campaign",
        campaignId: ad.campaign_id || "",
        adsetId: ad.adset_id || "",
        adsetName: ad.adset_name || "Unassigned group",
        urlTags: ad.url_tags || "",
        status: ad.status || "ACTIVE",
        channel: platform,
        ...(ad.campaign_type === undefined ? {} : { campaignType: ad.campaign_type }),
        customerId: ad.customer_id || "",
        validation: validateUtmParameters(
          {
            url_tags: ad.url_tags,
            advertising_channel_type: ad.advertising_channel_type,
            campaign_type: ad.campaign_type,
          },
          platform,
        ),
        spend: ad.spend,
        lcOrders: ad.lc_orders,
        lcRevenue: ad.lc_revenue,
      })),
    [ads, platform],
  );

  const totalCount = processedRows.length;
  const incorrectCount = useMemo(() => processedRows.filter((r) => r.validation.hasIssue).length, [processedRows]);
  const correctCount = totalCount - incorrectCount;

  /**
   * "Impact" — the real cost of the flagged ads' broken tracking, summed over the same
   * ads the "Auto-Correct with Agent" flow targets. `spend`/`lcOrders`/`lcRevenue` are the
   * SAME last-click computation the Meta/Google Campaign Attribution report shows for
   * this ad (see `utm-checker.ts`'s doc comment) — not a re-derivation.
   */
  const impactSummary = useMemo(() => {
    let spend = 0;
    let revenue = 0;
    let orders = 0;
    for (const row of processedRows) {
      if (!row.validation.hasIssue) continue;
      spend += row.spend;
      revenue += row.lcRevenue;
      orders += row.lcOrders;
    }
    return { spend, revenue, orders };
  }, [processedRows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return processedRows.filter((r) => {
      if (filterMode === "flagged" && !r.validation.hasIssue) return false;
      if (filterMode === "correct" && r.validation.hasIssue) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q) ||
        r.campaignName.toLowerCase().includes(q) ||
        r.adsetName.toLowerCase().includes(q) ||
        r.urlTags.toLowerCase().includes(q) ||
        r.validation.issues.some((i) => i.toLowerCase().includes(q))
      );
    });
  }, [processedRows, filterMode, search]);

  const eligibleFlaggedIds = useMemo(
    () => filteredRows.filter((r) => r.validation.hasIssue).map((r) => r.id),
    [filteredRows],
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = useMemo(
    () => filteredRows.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize),
    [filteredRows, page, pageSize],
  );

  const isAllSelected = eligibleFlaggedIds.length > 0 && selectedIds.size === eligibleFlaggedIds.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < eligibleFlaggedIds.length;

  const masterCheckboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (masterCheckboxRef.current) masterCheckboxRef.current.indeterminate = isIndeterminate;
  }, [isIndeterminate]);

  const handleToggleSelectAll = (checked: boolean): void => {
    setSelectedIds(checked ? new Set(eligibleFlaggedIds) : new Set());
  };

  const handleToggleRow = (id: string, checked: boolean): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleStartAutoFix = (): void => {
    setFilterMode("flagged");
    setIsSelectionMode(true);
    setSelectedIds(new Set());
    setPage(1);
  };

  const handleOpenConfirmModal = (): void => {
    if (selectedIds.size === 0) {
      showToast("Select at least one ad to auto-correct.", "info");
      return;
    }
    setConfirmInput("");
    setShowConfirmModal(true);
  };

  const handleCancelSelection = (): void => {
    setIsSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleExecuteAutoFix = async (): Promise<void> => {
    if (selectedIds.size === 0) {
      showToast("Select at least one ad to auto-correct.", "info");
      return;
    }
    const selectedAds = processedRows
      .filter((r) => r.validation.hasIssue && selectedIds.has(r.id))
      .map((r) => ({
        adId: r.id,
        campaignId: r.campaignId,
        adGroupId: r.adsetId,
        ...(r.campaignType === undefined ? {} : { campaignType: r.campaignType }),
        customerId: r.customerId,
        recommendedUrlTags: r.validation.fix,
      }));

    setAutoFixLoading(true);
    setAutoFixProgress("Starting auto-fix...");
    try {
      const BATCH_SIZE = 10;
      let totalFixed = 0;
      const totalRequested = selectedAds.length;
      let lastMessage = "";
      const totalBatches = Math.ceil(totalRequested / BATCH_SIZE);

      for (let i = 0; i < selectedAds.length; i += BATCH_SIZE) {
        const batch = selectedAds.slice(i, i + BATCH_SIZE);
        const batchIndex = Math.floor(i / BATCH_SIZE) + 1;
        const currentProcessedCount = Math.min(i + BATCH_SIZE, totalRequested);

        setAutoFixProgress(
          `Submitting batch ${batchIndex} of ${totalBatches} (${currentProcessedCount}/${totalRequested} ads)...`
        );

        const res = await client.post<{
          success: boolean;
          message: string;
          data: {
            success: boolean;
            fixedCount?: number;
            totalRequested?: number;
            message?: string;
            jobId?: string;
            status?: "queued" | "active" | "completed" | "failed";
          };
        }>("/api/v2/utm-checker/auto-fix", { platform, ads: batch, async: true });

        // If backend accepted the job asynchronously, poll its status
        if (res.data.data.status === "queued" && res.data.data.jobId) {
          const jobId = res.data.data.jobId;
          const MAX_POLLS = 150; // up to 300s (5 min) per batch
          let poll = 0;
          let batchCompleted = false;

          while (!batchCompleted && poll < MAX_POLLS) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            poll++;

            setAutoFixProgress(
              `Processing batch ${batchIndex} of ${totalBatches} (${currentProcessedCount}/${totalRequested} ads)...`
            );

            const statusRes = await client.get<{
              success: boolean;
              message: string;
              data: {
                jobId: string;
                status: "queued" | "active" | "completed" | "failed";
                result?: { success: boolean; fixedCount: number; totalRequested: number; message: string };
                failedReason?: string;
              };
            }>(`/api/v2/utm-checker/auto-fix/status/${jobId}`);

            const jobStatus = statusRes.data.data;
            if (jobStatus.status === "completed") {
              batchCompleted = true;
              if (jobStatus.result) {
                totalFixed += jobStatus.result.fixedCount;
                lastMessage = jobStatus.result.message;
              }
            } else if (jobStatus.status === "failed") {
              throw new Error(jobStatus.failedReason || `Auto-fix failed for batch ${batchIndex}`);
            }
          }

          if (!batchCompleted) {
            throw new Error(`Auto-fix timed out waiting for batch ${batchIndex}.`);
          }
        } else {
          // Synchronous fallback (if async was false or not supported)
          totalFixed += res.data.data.fixedCount ?? 0;
          lastMessage = res.data.data.message ?? "";
        }
      }

      const summaryMessage =
        totalRequested > BATCH_SIZE
          ? `Successfully auto-corrected ${totalFixed} out of ${totalRequested} ${platform === "meta" ? "Meta" : "Google"} ads.`
          : lastMessage;

      showToast(summaryMessage, totalFixed === totalRequested ? "success" : "info");
      setShowConfirmModal(false);
      setConfirmInput("");
      setIsSelectionMode(false);
      setSelectedIds(new Set());
      void loadAds(false);
    } catch (caught) {
      showToast(apiErrorMessage(caught, "Could not auto-correct the selected ads. Try again."), "error");
    } finally {
      setAutoFixLoading(false);
      setAutoFixProgress("");
    }
  };

  const handleCopyFix = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      showToast("Copied the corrected UTM parameters.", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch (caught) {
      showToast(apiErrorMessage(caught, "Could not copy to the clipboard."), "error");
    }
  };

  const rangeStart = filteredRows.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, filteredRows.length);
  const colSpan = isSelectionMode ? 5 : 4;

  return (
    <section className="flex min-w-0 flex-col gap-5">
      {/* Toasts */}
      <div className="pointer-events-none fixed top-5 right-5 z-[60] flex flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              "modal-content-animate bg-card pointer-events-auto flex items-center gap-2 rounded-lg border px-4 py-2.5 text-xs font-medium shadow-lg",
              t.type === "success" && "border-emerald-500/40 text-emerald-700 dark:text-emerald-300",
              t.type === "error" && "border-destructive/40 text-destructive",
              t.type === "info" && "border-primary/40 text-primary",
            )}
          >
            {t.type === "success" && <CheckCircle2 className="size-4 shrink-0 text-emerald-600" />}
            {t.type === "error" && <AlertCircle className="text-destructive size-4 shrink-0" />}
            {t.type === "info" && <Info className="text-primary size-4 shrink-0" />}
            <span>{t.message}</span>
          </div>
        ))}
      </div>

      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 text-2xl font-bold tracking-tight">
            <BarChart3 className="text-primary size-6" />
            UTM Checker
            <ReportGuideToggleButton
              showGuide={showGuideSection}
              onToggle={() => setShowGuideSection((current) => !current)}
            />
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Audit dynamic URL parameters, validate mandatory tracking tags, and generate copy-paste fixes for every active
            Meta &amp; Google ad.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => void handleSyncAndReaudit()}
            disabled={syncing || loading}
            className={cn(
              "flex h-9 items-center gap-2 rounded-md px-4 text-sm font-medium text-white shadow-xs transition-all",
              syncing || loading
                ? "cursor-not-allowed bg-[#5e5bf7]/70 opacity-80"
                : "cursor-pointer bg-[#5e5bf7] hover:bg-[#4f4bf5]",
            )}
          >
            <RefreshCw className={cn("size-3.5", syncing && "animate-spin")} />
            <span>{syncProgress || (syncing ? "Syncing ads…" : "Sync & Re-audit")}</span>
          </button>

          <Select
            value={platform}
            onChange={(e) => setPlatform(e.target.value as "meta" | "google")}
            className="h-9 w-36 text-xs font-medium"
            aria-label="Ad platform"
          >
            <option value="meta">Meta Ads</option>
            <option value="google">Google Ads</option>
          </Select>
        </div>
      </header>

      {/*
        Guide content mirrors `ecom_code`'s `UtmCheckerReportNotes`/`UtmCheckerCalculationNotes`
        (same intro line, same "Validation"/"Sync & Re-audit" pair of cards). ADAPTED in one
        way: legacy's guide text doesn't mention "Impact" or "Auto-Correct with Agent" at
        all, but both are real, prominent features of THIS screen (the Impact bar, the
        bulk auto-fix flow) — described here since a guide that omits the report's most
        visible numbers would be incomplete, not because legacy has equivalent copy to port.
      */}
      <ReportGuideAccordion
        showGuide={showGuideSection}
        whatContent={
          <div className={noteText}>
            <p className="m-0">
              UTM Checker audits every active Meta or Google ad&apos;s destination URL to make sure it carries the
              tracking parameters our attribution pipeline depends on:
            </p>
            <HighlightGrid cols={2}>
              <HighlightCard tone="blue" title="Validation">
                Each active ad&apos;s URL is checked for the mandatory dynamic UTM parameters (campaign/adset/ad IDs,
                source, etc.) and flagged if any are missing or broken.
              </HighlightCard>
              <HighlightCard tone="purple" title="Sync & Re-audit">
                Use "Sync & Re-audit" to pull the latest active ad set from Meta/Google and re-run the URL audit against
                the freshest data.
              </HighlightCard>
            </HighlightGrid>
            <p className="m-0">
              <strong>Impact</strong> — shown when any ad is flagged — is the revenue, orders and ad spend that
              misattribution costs while those tags stay broken. <strong>Auto-Correct with Agent</strong> rewrites the
              flagged ads&apos; UTM parameters directly on the ad platform to the corrected string shown in the fix
              modal.
            </p>
          </div>
        }
        howTitle="How each ad's UTM status is determined"
        howContent={
          <div className={noteText}>
            <FormulaList>
              <li>
                <strong>Correct:</strong> the ad&apos;s destination URL contains every mandatory tracking parameter,
                correctly formatted.
              </li>
              <li>
                <strong>Incorrect:</strong> one or more mandatory parameters are missing, misspelled, or hard-coded
                instead of dynamic — these break attribution for that ad&apos;s traffic.
              </li>
              <li>
                <strong>Copy-paste fix:</strong> for incorrect ads, a corrected URL string is generated so it can be
                pasted straight back into the Meta/Google ad settings.
              </li>
              <li>
                <strong>Impact:</strong> sums Last-Click spend, orders and revenue — over all-time, since{" "}
                {IMPACT_WINDOW_START} — for the flagged ads only, using the same Last-Click computation Meta/Google
                Campaign Attribution shows for that ad.
              </li>
            </FormulaList>
          </div>
        }
      />

      <hr className="border-border" />

      {/*
        Impact bar — now ALWAYS shown, not just when the "flagged"/"all" filter has at
        least one incorrect ad. The Impact tooltip already says this is flagged-ad data,
        so there is nothing misleading about it staying on screen while viewing
        "correct" ads or a store with zero incorrect ones — it just reads as all zeros,
        which is itself the answer ("no revenue at risk").
        LEFT = the "You have N active ads — X correct, Y incorrect" summary. RIGHT =
        "Impact" grouped with the very revenue/orders/spend figures it is the label
        for (owner request) — Impact and its own numbers read as one thing, rather
        than the label sitting on its own with the ad-count summary in between it and
        the numbers it describes.
      */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-3.5 text-xs dark:border-red-900/50 dark:bg-red-950/40">
        <span className="text-muted-foreground font-normal">
          You have <strong className="text-foreground font-semibold">{totalCount} active ads</strong> —{" "}
          <span className="font-semibold text-emerald-600">{correctCount} correct</span>,{" "}
          <span className="text-destructive font-semibold">{incorrectCount} incorrect</span>.
          {/* Commented out, not deleted — owner request: this line doesn't need its
              own info icon now that Impact's own one covers the same ground. */}
          {/* <InfoTooltip text="Audit results are based on the mandatory UTM tags and how each parameter is formatted." /> */}
        </span>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <div className="flex items-center gap-2.5">
            <span className="bg-destructive flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
              !
            </span>
            <span className="text-foreground flex items-center gap-1.5 text-xs font-bold">
              Impact
              <InfoTooltip text="Total revenue, orders and ad spend across the flagged ads that are at risk of misattribution from missing or invalid UTM tags." />
            </span>
          </div>
          <span className="text-border hidden md:inline">•</span>
          <div className="text-foreground text-xs font-medium">
            INR <span className="text-destructive font-semibold">{formatMoney(impactSummary.revenue)}</span> revenue
            untracked
          </div>
          <span className="text-border hidden md:inline">•</span>
          <div className="text-foreground text-xs font-medium">
            <span className="text-destructive font-semibold">{formatCount(impactSummary.orders)}</span> orders untracked
          </div>
          <span className="text-border hidden md:inline">•</span>
          <div className="text-foreground text-xs font-medium">
            ₹ <span className="text-destructive font-semibold">{formatMoney(impactSummary.spend)}</span> ad spend
            untracked
          </div>
        </div>
      </div>

      {error !== null && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}

      {/* Table card */}
      <div className="bg-card min-w-0 overflow-hidden rounded-xl border shadow-sm">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 border-b p-3">
          <h2 className="text-foreground text-sm font-semibold">
            {filterMode === "correct"
              ? "Correct parameters"
              : filterMode === "flagged"
                ? "Incorrect parameters"
                : "Parameters audit"}
          </h2>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs font-medium">Filter</span>
            <Select
              value={filterMode}
              onChange={(e) => {
                setFilterMode(e.target.value as FilterMode);
                setPage(1);
              }}
              className="h-9 w-[9.5rem] text-xs"
              aria-label="Filter ads"
            >
              <option value="all">All ads ({totalCount})</option>
              <option value="flagged">Flagged ({incorrectCount})</option>
              <option value="correct">Correct ({correctCount})</option>
            </Select>
          </div>

          {isSelectionMode ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelSelection}
                disabled={autoFixLoading}
                className="h-9 text-xs"
              >
                Cancel
              </Button>
              <button
                type="button"
                onClick={handleOpenConfirmModal}
                disabled={selectedIds.size === 0 || autoFixLoading}
                className="flex h-9 items-center gap-1.5 rounded-md bg-[#5e5bf7] px-3.5 text-xs font-medium text-white shadow-xs transition-colors hover:bg-[#4f4bf5] disabled:pointer-events-none disabled:opacity-50 cursor-pointer"
              >
                <Zap className="size-3.5 fill-current" />
                Proceed &amp; auto-correct ({selectedIds.size})
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleStartAutoFix}
              disabled={incorrectCount === 0 || loading}
              className={cn(
                "flex h-9 items-center gap-1.5 rounded-md px-3.5 text-xs font-semibold shadow-xs transition-colors",
                incorrectCount === 0 || loading
                  ? "bg-muted text-muted-foreground cursor-not-allowed"
                  : "cursor-pointer bg-[#1890ff] text-white hover:bg-[#096dd9]",
              )}
            >
              <Zap className="size-3.5 fill-current" />
              Auto-Correct with Agent ({incorrectCount})
            </button>
          )}

          <div className="relative w-full sm:w-56">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
            <Input
              type="search"
              placeholder="Search…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="h-9 pl-8 text-xs"
              aria-label="Search ads"
            />
          </div>
        </div>

        {/* Table */}
        <div className="max-h-[min(65svh,44rem)] overflow-auto">
          <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
            <colgroup>
              {colWidths.map((w, i) => (
                <col key={i} style={{ width: `${w}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {isSelectionMode && (
                  <th className="row-head text-muted-foreground sticky top-0 left-0 z-30 h-11 w-10 border-b border-r border-border px-3 text-center text-[13px] font-medium relative">
                    <input
                      type="checkbox"
                      ref={masterCheckboxRef}
                      checked={isAllSelected}
                      onChange={(e) => handleToggleSelectAll(e.target.checked)}
                      className="accent-primary cursor-pointer align-middle"
                      aria-label="Select all flagged ads"
                    />
                    <TableResizeHandle onResize={(e) => startResize(0, e)} />
                  </th>
                )}
                <th
                  className={cn(
                    "row-head text-foreground sticky top-0 z-20 h-11 border-b border-r border-border px-3 text-left text-[13px] font-semibold relative",
                    !isSelectionMode && "left-0 z-30",
                  )}
                >
                  Ad ID
                  <TableResizeHandle onResize={(e) => startResize(isSelectionMode ? 1 : 0, e)} />
                </th>
                <th className="row-head text-foreground sticky top-0 z-20 h-11 border-b border-r border-border px-3 text-left text-[13px] font-semibold relative">
                  Ad name
                  <TableResizeHandle onResize={(e) => startResize(isSelectionMode ? 2 : 1, e)} />
                </th>
                <th className="row-head text-foreground sticky top-0 z-20 h-11 border-b border-r border-border px-3 text-left text-[13px] font-semibold relative">
                  Campaign name
                  <TableResizeHandle onResize={(e) => startResize(isSelectionMode ? 3 : 2, e)} />
                </th>
                <th className="row-head text-foreground sticky top-0 z-20 h-11 border-b border-r border-border px-3 text-center text-[13px] font-semibold relative">
                  {filterMode === "correct" ? "Details" : "How to fix this"}
                  <TableResizeHandle onResize={(e) => startResize(isSelectionMode ? 4 : 3, e)} />
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && paginatedRows.length === 0 ? (
                Array.from({ length: 8 }, (_, i) => (
                  <tr key={i} className={i % 2 === 1 ? "row-odd" : "row-even"}>
                    {isSelectionMode && (
                      <td className="border-b px-3 py-3">
                        <Skeleton className="size-4" />
                      </td>
                    )}
                    <td className="border-b px-3 py-3">
                      <Skeleton className="h-4 w-40" />
                    </td>
                    <td className="border-b px-3 py-3">
                      <Skeleton className="h-4 w-52" />
                    </td>
                    <td className="border-b px-3 py-3">
                      <Skeleton className="h-4 w-40" />
                    </td>
                    <td className="border-b px-3 py-3 text-center">
                      <Skeleton className="mx-auto h-4 w-12" />
                    </td>
                  </tr>
                ))
              ) : paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="text-muted-foreground px-4 py-14 text-center text-sm">
                    No ads match the current filter or search for {storeName}.
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row, idx) => {
                  const checked = selectedIds.has(row.id);
                  const zebra = idx % 2 === 1 ? "row-odd" : "row-even";
                  return (
                    <tr key={row.id} className={cn("transition-colors", checked ? "bg-primary/5" : zebra)}>
                      {isSelectionMode && (
                        <td
                          className={cn(
                            "sticky left-0 z-10 border-b border-r px-3 py-3 text-center",
                            checked ? "bg-primary/5" : zebra,
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!row.validation.hasIssue}
                            onChange={(e) => handleToggleRow(row.id, e.target.checked)}
                            className="accent-primary cursor-pointer align-middle disabled:opacity-40"
                            aria-label={`Select ${row.name}`}
                          />
                        </td>
                      )}
                      <td
                        className={cn(
                          "border-b px-3 py-3",
                          !isSelectionMode && `sticky left-0 z-10 border-r ${checked ? "bg-primary/5" : zebra}`,
                        )}
                      >
                        <div className="flex items-center gap-2">
                          {row.validation.hasIssue ? (
                            <XCircle className="size-4 shrink-0 text-red-500" />
                          ) : (
                            <CheckCircle2 className="size-4 shrink-0 text-emerald-500" />
                          )}
                          <span className="text-foreground max-w-44 truncate font-mono text-xs font-medium" title={row.id}>
                            {row.id}
                          </span>
                        </div>
                      </td>
                      <td className="border-b px-3 py-3">
                        <span className="text-foreground block max-w-[22rem] truncate text-xs font-medium" title={row.name}>
                          {row.name}
                        </span>
                      </td>
                      <td className="border-b px-3 py-3">
                        <span
                          className="text-muted-foreground block max-w-[18rem] truncate text-xs"
                          title={row.campaignName}
                        >
                          {row.campaignName}
                        </span>
                      </td>
                      <td className="border-b px-3 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => setSelectedAd(row)}
                          className="text-primary text-xs font-medium underline-offset-2 hover:underline"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col gap-3 border-t p-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            {isSelectionMode ? (
              <div className="border-primary/25 bg-primary/5 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
                <div className="flex items-center gap-2.5">
                  <span className="bg-primary flex size-6 shrink-0 items-center justify-center rounded-full text-white">
                    <Zap className="size-3.5 fill-current" />
                  </span>
                  <div>
                    <h4 className="text-foreground mb-0.5 text-xs font-bold">Select ads to auto-correct</h4>
                    <p className="text-muted-foreground text-[11px]">
                      Tick the ads to correct, then press{" "}
                      <strong className="text-foreground font-semibold">Proceed &amp; auto-correct</strong>.
                    </p>
                  </div>
                </div>
                <span className="bg-card text-foreground rounded-md border px-2.5 py-1 text-xs font-semibold">
                  {selectedIds.size} of {incorrectCount} selected
                </span>
              </div>
            ) : (
              <span className="text-muted-foreground text-xs">
                Showing {rangeStart}–{rangeEnd} of {filteredRows.length} active ads
              </span>
            )}
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-xs">Rows</span>
              <Select
                value={String(pageSize)}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="h-8 w-[4.5rem] text-xs"
                aria-label="Rows per page"
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="h-8 text-xs"
              >
                Previous
              </Button>
              <span className="text-muted-foreground px-2 text-xs font-medium">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="h-8 text-xs"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Fix modal */}
      {selectedAd !== null && (
        <div
          className="modal-backdrop-animate fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]"
          onClick={() => setSelectedAd(null)}
        >
          <div
            className="modal-content-animate bg-card flex max-h-[90vh] w-full max-w-[720px] flex-col gap-5 overflow-y-auto rounded-2xl border p-6 text-xs shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-foreground text-base font-bold">
                {selectedAd.validation.hasIssue ? "Fix UTM parameters" : "UTM parameter details"}
              </h3>
              <button
                type="button"
                onClick={() => setSelectedAd(null)}
                className="text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg p-1.5 transition-colors"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>

            <div className="bg-muted/40 grid grid-cols-1 gap-4 rounded-xl border p-4 text-xs md:grid-cols-2">
              <div>
                <div className="text-primary mb-0.5 font-semibold">Ad ID</div>
                <div className="text-foreground font-mono font-medium">{selectedAd.id}</div>
              </div>
              <div>
                <div className="text-primary mb-0.5 font-semibold">Ad name</div>
                <div className="text-foreground font-medium break-words">{selectedAd.name}</div>
              </div>
              <div>
                <div className="text-primary mb-0.5 font-semibold">Campaign name</div>
                <div className="text-foreground font-medium break-words">
                  {selectedAd.campaignName}
                  {selectedAd.campaignId && selectedAd.campaignId !== selectedAd.campaignName && (
                    <span className="text-muted-foreground ml-1 font-normal">({selectedAd.campaignId})</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-primary mb-0.5 font-semibold">Metrics</div>
                <div className="text-foreground font-medium">
                  {selectedAd.validation.hasIssue ? (
                    <>
                      Ad spend untracked:{" "}
                      <span className="text-destructive font-semibold">INR {formatMoney(selectedAd.spend)}</span>
                    </>
                  ) : (
                    <>
                      Ad spend: <span className="font-semibold text-emerald-600">INR {formatMoney(selectedAd.spend)}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-primary text-xs font-semibold">Current UTM parameters</span>
                  {!selectedAd.validation.hasIssue && (
                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                      ✓ Correctly formatted
                    </span>
                  )}
                </div>
                <div className="bg-muted/40 text-muted-foreground rounded-lg border p-3 font-mono text-[11px] break-all">
                  {selectedAd.urlTags || "No UTM parameters configured"}
                </div>
              </div>

              {selectedAd.validation.hasIssue ? (
                <>
                  <div>
                    <div className="text-primary mb-1 text-xs font-semibold">Incorrect UTM parameters</div>
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-950/40">
                      <div className="text-foreground mb-1 font-medium">Missing or invalid:</div>
                      <ul className="text-destructive list-inside list-disc space-y-1 text-xs font-medium">
                        {selectedAd.validation.issues.map((issue, i) => (
                          <li key={i} className="break-words">
                            {issue}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div>
                    <div className="text-primary mb-1 text-xs font-semibold">Corrected UTM string</div>
                    <div className="bg-muted/40 text-foreground flex items-center justify-between gap-3 rounded-lg border p-3 font-mono text-[11px] break-all">
                      <span>{selectedAd.validation.fix}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void handleCopyFix(selectedAd.validation.fix)}
                        className="text-muted-foreground size-7 shrink-0 p-0"
                        title="Copy to clipboard"
                      >
                        {copied ? <Check className="size-4 text-emerald-600" /> : <Copy className="size-4" />}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50 p-3.5 dark:border-emerald-800 dark:bg-emerald-950/40">
                  <CheckCircle2 className="size-4 shrink-0 text-emerald-600" />
                  <div className="text-xs font-medium text-emerald-800 dark:text-emerald-300">
                    Every mandatory tracking parameter and tag is correctly formatted. No fixes needed.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auto-Fix Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="modal-backdrop-animate fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
          onClick={() => {
            if (!autoFixLoading) {
              setShowConfirmModal(false);
              setConfirmInput("");
            }
          }}
        >
          <div
            className="modal-content-animate bg-card flex max-h-[90vh] w-full max-w-[620px] flex-col gap-5 overflow-y-auto rounded-2xl border p-6 sm:p-7 text-xs shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="size-5" />
                </span>
                <div>
                  <h3 className="text-foreground text-base font-bold">Confirm UTM Auto-Correction</h3>
                  <p className="text-muted-foreground text-xs mt-0.5">
                    Update tracking parameters on live ad platforms
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={autoFixLoading}
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmInput("");
                }}
                className="text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg p-1.5 transition-colors disabled:opacity-50"
                aria-label="Close"
              >
                <X className="size-4" />
              </button>
            </div>

            <div className="space-y-3">
              <p className="text-foreground text-sm leading-relaxed whitespace-normal sm:whitespace-nowrap">
                Are you sure you want to correct the UTM parameters for{" "}
                <strong className="text-primary font-semibold">
                  {selectedIds.size} selected {platform === "meta" ? "Meta" : "Google"} ad{selectedIds.size === 1 ? "" : "s"}
                </strong>?
              </p>

              <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 p-3.5 text-xs text-amber-900 dark:text-amber-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="size-4 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div className="space-y-1">
                    <p className="font-semibold text-xs">Important: Ad Learning Phase Notice</p>
                    <p className="text-xs leading-relaxed opacity-90">
                      Updating tracking parameters modifies the live ad creative on {platform === "meta" ? "Meta Ads Manager" : "Google Ads"}. This update may reset optimization and put active ads back into the <strong>learning phase</strong>.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-2 space-y-2">
                <label htmlFor="confirm-input" className="block text-xs font-medium text-foreground">
                  To confirm this action, please type{" "}
                  <span className="font-semibold text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded text-[12px] font-mono select-all">
                    Confirm
                  </span>{" "}
                  below:
                </label>
                <Input
                  id="confirm-input"
                  type="text"
                  autoFocus
                  disabled={autoFixLoading}
                  placeholder='Type "Confirm" to proceed'
                  value={confirmInput}
                  onChange={(e) => setConfirmInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && confirmInput.trim() === "Confirm" && !autoFixLoading) {
                      e.preventDefault();
                      void handleExecuteAutoFix();
                    }
                  }}
                  className={
                    confirmInput.trim().length > 0 && confirmInput.trim() !== "Confirm"
                      ? "h-10 text-sm border-destructive focus-visible:ring-destructive"
                      : "h-10 text-sm"
                  }
                />
                {confirmInput.trim().length > 0 && confirmInput.trim() !== "Confirm" && (
                  <p className="text-[11px] text-destructive flex items-center gap-1 mt-1">
                    <AlertCircle className="size-3.5 shrink-0" />
                    Case-sensitive: please type &quot;Confirm&quot; exactly (capital &apos;C&apos;) to proceed.
                  </p>
                )}
              </div>
            </div>

            {autoFixLoading && autoFixProgress && (
              <div className="flex items-center gap-2.5 rounded-xl border border-primary/20 bg-primary/5 p-3 text-xs text-primary font-medium animate-pulse">
                <Loader2 className="size-4 animate-spin shrink-0 text-primary" />
                <span>{autoFixProgress}</span>
              </div>
            )}
            <div className="flex items-center justify-end gap-2.5 pt-2 border-t">
              <Button
                type="button"
                variant="outline"
                disabled={autoFixLoading}
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmInput("");
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={confirmInput.trim() !== "Confirm" || autoFixLoading}
                onClick={() => void handleExecuteAutoFix()}
                className="bg-[#5e5bf7] hover:bg-[#4f4bf5] text-white gap-2"
              >
                {autoFixLoading ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Correcting ads...
                  </>
                ) : (
                  <>
                    <Zap className="size-3.5 fill-current" />
                    Confirm &amp; Auto-Correct ({selectedIds.size})
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
